/**
 * Notification Settings — shadcn/Tailwind
 * Tenant notification preferences with channel management and template management
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../components/ui/use-toast';
import {
  Settings, Bell, Mail, MessageSquare, Phone, Send, Eye, RefreshCw, FlaskConical,
  CheckCircle, AlertCircle, Info, CircleDot,
} from 'lucide-react';
import {
  fetchNotificationChannels, fetchNotificationTemplates,
  fetchTenantNotificationSettings, updateTenantNotificationSettings,
  sendTestNotification,
} from '../../api/notifications';
import { PageHeader } from '../ui/page-header';
import { rowOpenProps } from '@/lib/rowOpen';

// Types
interface NotificationChannel {
  channel_id: string; name: string; channel_type: 'email' | 'sms' | 'whatsapp';
  provider: string; is_active: boolean; health_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  rate_limit_per_minute: number; cost_per_message: number;
  messages_sent_today: number; messages_sent_month: number;
}

interface NotificationTemplate {
  template_id: string; name: string; template_type: string;
  channel_type: 'email' | 'sms' | 'whatsapp'; context: 'enterprise' | 'tenant' | 'both';
  subject?: string; content: string; html_content?: string;
  is_active: boolean; usage_count: number; last_used?: string;
}

interface TenantNotificationSetting {
  settings_id: string; tenant_id: string; notification_type: string; description: string;
  is_enabled: boolean; default_channel_type: 'email' | 'sms' | 'whatsapp';
  send_to_patients: boolean; send_to_staff: boolean; patient_opt_out_allowed: boolean;
  patient_communication_hours?: { start_hour: number; end_hour: number; timezone: string };
  schedule_type: 'immediate' | 'scheduled' | 'recurring'; schedule_config?: any;
}

const channelIcon = (type: string) => {
  switch (type) {
    case 'email': return <Mail className="h-4 w-4" />;
    case 'sms': return <MessageSquare className="h-4 w-4" />;
    case 'whatsapp': return <Phone className="h-4 w-4" />;
    default: return <Bell className="h-4 w-4" />;
  }
};

const healthBadge = (status: string) => {
  const map: Record<string, { variant: 'default' | 'secondary' | 'destructive'; icon: React.ReactNode; label: string }> = {
    healthy: { variant: 'default', icon: <CheckCircle className="h-3 w-3" />, label: 'Healthy' },
    degraded: { variant: 'secondary', icon: <AlertCircle className="h-3 w-3" />, label: 'Degraded' },
    unhealthy: { variant: 'destructive', icon: <AlertCircle className="h-3 w-3" />, label: 'Unhealthy' },
    unknown: { variant: 'secondary', icon: <Info className="h-3 w-3" />, label: 'Unknown' },
  };
  const c = map[status] || map.unknown;
  return <Badge variant={c.variant} className="gap-1">{c.icon}{c.label}</Badge>;
};

// Channel Management
const ChannelManagement: React.FC = () => {
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<NotificationChannel | null>(null);
  const [testRecipient, setTestRecipient] = useState('');

  const loadChannels = async () => {
    setLoading(true);
    try { setChannels(await fetchNotificationChannels()); }
    catch { toast({ title: 'Failed to load channels', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadChannels(); }, []);

  const handleTest = (ch: NotificationChannel) => { setSelectedChannel(ch); setTestRecipient(''); setTestModalOpen(true); };

  const handleSendTest = async () => {
    if (!selectedChannel) return;
    setLoading(true);
    try {
      await sendTestNotification(selectedChannel.channel_id, testRecipient);
      toast({ title: 'Test notification sent', variant: 'success' });
      setTestModalOpen(false);
    } catch { toast({ title: 'Failed to send test', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <span className="font-semibold">Notification Channels</span>
          <Button variant="outline" size="sm" onClick={loadChannels} disabled={loading}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </div>
        <div className="p-4">
          <div className="bg-info-100 border border-info-200 rounded-md p-3 mb-4 flex items-center gap-2">
            <Info className="h-4 w-4 text-info" />
            <p className="text-sm text-foreground">Notification channels are configured by system administrators.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Channel</TableHead><TableHead>Status</TableHead><TableHead>Usage Today</TableHead><TableHead>Cost</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {channels.map((ch) => (
                <TableRow key={ch.channel_id}>
                  <TableCell><div className="flex items-center gap-2">{channelIcon(ch.channel_type)}<div><span className="font-medium">{ch.name}</span><br /><span className="text-xs text-muted-foreground">{ch.provider} &bull; {ch.channel_type.toUpperCase()}</span></div></div></TableCell>
                  <TableCell>{healthBadge(ch.health_status)}</TableCell>
                  <TableCell><div><span>{ch.messages_sent_today}</span><br /><span className="text-xs text-muted-foreground">Limit: {ch.rate_limit_per_minute}/min</span></div></TableCell>
                  <TableCell><span className="text-muted-foreground">${ch.cost_per_message.toFixed(4)}/msg</span></TableCell>
                  <TableCell><Switch checked={ch.is_active} disabled /></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleTest(ch)} disabled={!ch.is_active}><FlaskConical className="h-3 w-3 mr-1" />Test</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Test {selectedChannel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{selectedChannel?.channel_type === 'email' ? 'Test Email Address' : 'Test Phone Number'} *</Label>
              <Input placeholder={selectedChannel?.channel_type === 'email' ? 'test@example.com' : '+1234567890'} value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTestModalOpen(false)}>Cancel</Button><Button onClick={handleSendTest} disabled={loading}><Send className="h-4 w-4 mr-1" />Send Test</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Template Management
const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    try { setTemplates(await fetchNotificationTemplates({ context: 'tenant' })); }
    catch { toast({ title: 'Failed to load templates', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTemplates(); }, []);

  const handlePreview = (t: NotificationTemplate) => {
    setSelectedTemplate(t);
    setPreviewOpen(true);
  };

  const channelColor: Record<string, string> = { email: 'bg-info-100 text-info-700', sms: 'bg-success-100 text-success-700', whatsapp: 'bg-info-100 text-info-700' };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <span className="font-semibold">Notification Templates</span>
          <Button variant="outline" size="sm" onClick={loadTemplates} disabled={loading}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </div>
        <div className="p-4">
          <div className="bg-info-100 border border-info-200 rounded-md p-3 mb-4 flex items-center gap-2">
            <Info className="h-4 w-4 text-info" />
            <p className="text-sm text-foreground">These templates are used for automated notifications. Contact support to customize templates.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Template</TableHead><TableHead>Channel</TableHead><TableHead>Usage</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.template_id} {...rowOpenProps(() => handlePreview(t))}>
                  <TableCell><div className="flex items-center gap-2">{channelIcon(t.channel_type)}<div><span className="font-medium">{t.name}</span><br /><span className="text-xs text-muted-foreground">{t.template_type.replace(/_/g, ' ').toUpperCase()}</span></div></div></TableCell>
                  <TableCell><Badge className={channelColor[t.channel_type]}>{t.channel_type.toUpperCase()}</Badge></TableCell>
                  <TableCell><div><span>{t.usage_count} times</span>{t.last_used && <><br /><span className="text-xs text-muted-foreground">Last: {new Date(t.last_used).toLocaleDateString()}</span></>}</div></TableCell>
                  <TableCell><Badge variant={t.is_active ? 'default' : 'secondary'}>{t.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handlePreview(t)}><Eye className="h-3 w-3 mr-1" />Preview</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Preview: {selectedTemplate?.name}</DialogTitle></DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              {selectedTemplate.subject && <div><p className="font-medium text-sm">Subject:</p><div className="bg-muted p-2 rounded text-sm">{selectedTemplate.subject}</div></div>}
              <div><p className="font-medium text-sm">Content:</p><div className="bg-muted p-3 rounded text-sm whitespace-pre-wrap">{selectedTemplate.content}</div></div>
              {selectedTemplate.html_content && <div><p className="font-medium text-sm">HTML Content:</p><div className="bg-muted p-3 rounded text-sm border" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedTemplate.html_content) }} /></div>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Notification Settings Component
const NotificationSettings: React.FC = () => {
  const [, setSettings] = useState<TenantNotificationSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await fetchTenantNotificationSettings();
      setSettings(data);
      const fv: Record<string, any> = {};
      data.forEach((s: TenantNotificationSetting) => {
        fv[s.notification_type] = {
          is_enabled: s.is_enabled, default_channel_type: s.default_channel_type,
          send_to_patients: s.send_to_patients, send_to_staff: s.send_to_staff,
          patient_opt_out_allowed: s.patient_opt_out_allowed,
          patient_communication_hours: s.patient_communication_hours,
        };
      });
      setFormValues(fv);
    } catch { toast({ title: 'Failed to load notification settings', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const updates = Object.keys(formValues).map((key) => ({ notification_type: key, ...formValues[key] }));
      for (const u of updates) await updateTenantNotificationSettings(u);
      toast({ title: 'Notification settings saved', variant: 'success' });
      loadSettings();
    } catch { toast({ title: 'Failed to save settings', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const updateSetting = (type: string, field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const notificationTypes = [
    { key: 'appointment_reminder', title: 'Appointment Reminders', description: 'Notify patients about upcoming appointments', icon: <Bell className="h-4 w-4" /> },
    { key: 'test_results_available', title: 'Test Results Available', description: 'Notify patients when lab results are ready', icon: <FlaskConical className="h-4 w-4" /> },
    { key: 'billing_statement', title: 'Billing Statements', description: 'Send billing statements and payment reminders', icon: <Mail className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification Settings"
        icon={<Settings className="h-6 w-6" />}
        description="Configure how your organization sends notifications to patients and staff."
      />

      <Tabs defaultValue="settings">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="settings"><Settings className="h-3 w-3 mr-1" />Settings</TabsTrigger>
          <TabsTrigger value="channels"><CircleDot className="h-3 w-3 mr-1" />Channels</TabsTrigger>
          <TabsTrigger value="templates"><Bell className="h-3 w-3 mr-1" />Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4 space-y-4">
          {notificationTypes.map((nt) => (
            <Card key={nt.key} className="p-4">
              <div className="flex items-center gap-2 mb-3">{nt.icon}<span className="font-semibold">{nt.title}</span></div>
              <p className="text-sm text-muted-foreground mb-4">{nt.description}</p>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="flex items-center gap-2"><Switch checked={formValues[nt.key]?.is_enabled ?? false} onCheckedChange={(v) => updateSetting(nt.key, 'is_enabled', v)} /><Label className="text-xs">Enable/Disable</Label></div>
                </div>
                <div>
                  <Select value={formValues[nt.key]?.default_channel_type || 'email'} onValueChange={(v) => updateSetting(nt.key, 'default_channel_type', v)}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Default Channel</p>
                </div>
                <div>
                  <div className="flex items-center gap-2"><Switch checked={formValues[nt.key]?.send_to_patients ?? false} onCheckedChange={(v) => updateSetting(nt.key, 'send_to_patients', v)} /><Label className="text-xs">Patients</Label></div>
                </div>
                <div>
                  <div className="flex items-center gap-2"><Switch checked={formValues[nt.key]?.send_to_staff ?? false} onCheckedChange={(v) => updateSetting(nt.key, 'send_to_staff', v)} /><Label className="text-xs">Staff</Label></div>
                </div>
              </div>
              <Separator className="my-3" />
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={formValues[nt.key]?.patient_opt_out_allowed ?? false} onCheckedChange={(v) => updateSetting(nt.key, 'patient_opt_out_allowed', v)} />
                  <Label className="text-xs">Allow patient opt-out</Label>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Start Hour</Label>
                  <Input type="number" min={0} max={23} className="h-8" value={formValues[nt.key]?.patient_communication_hours?.start_hour ?? 8} onChange={(e) => updateSetting(nt.key, 'patient_communication_hours', { ...formValues[nt.key]?.patient_communication_hours, start_hour: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">End Hour</Label>
                  <Input type="number" min={0} max={23} className="h-8" value={formValues[nt.key]?.patient_communication_hours?.end_hour ?? 20} onChange={(e) => updateSetting(nt.key, 'patient_communication_hours', { ...formValues[nt.key]?.patient_communication_hours, end_hour: parseInt(e.target.value) })} />
                </div>
              </div>
            </Card>
          ))}
          <div className="flex justify-end"><Button onClick={handleSave} disabled={loading}>Save Settings</Button></div>
        </TabsContent>

        <TabsContent value="channels" className="mt-4"><ChannelManagement /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplateManagement /></TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationSettings;
